import { useState, useCallback, useMemo } from "react";
import {
    LEARNING_MODES,
    getModeGroup,
    getTargetsForMode,
    findTargetConfig,
    DEFAULT_TARGET_BY_GROUP,
} from "../constants/learningModes";

const LEARNING_MODE_KEY = "chatbot:learningMode";
// Lưu target riêng theo từng nhóm: { math, programming, research }
const EXAM_TARGET_KEY = "chatbot:targetByGroup";
const QUIZ_STATE_KEY = "chatbot:quizState";

const LEGACY_EXAM_TARGET_KEY = "chatbot:examTarget";

/** Đọc map target theo nhóm từ localStorage, có fallback giá trị cũ. */
function loadTargetByGroup() {
    const base = { ...DEFAULT_TARGET_BY_GROUP };
    try {
        const saved = localStorage.getItem(EXAM_TARGET_KEY);
        if (saved) return { ...base, ...JSON.parse(saved) };
        // Migrate giá trị examTarget cũ (chỉ áp dụng cho nhóm math).
        const legacy = localStorage.getItem(LEGACY_EXAM_TARGET_KEY);
        if (legacy) base.math = legacy;
    } catch {
        /* ignore */
    }
    return base;
}

/**
 * usePimathTools — Hook quản lý state cho các chức năng PIMATH.
 * Bao gồm: Learning Mode, Exam Target, Quiz State.
 */
export function usePimathTools() {
    // ── Learning Mode ─────────────────────────────────────────────────────────
    const [learningMode, setLearningModeState] = useState(() => {
        try {
            return localStorage.getItem(LEARNING_MODE_KEY) || "detailed";
        } catch {
            return "detailed";
        }
    });

    const setLearningMode = useCallback((mode) => {
        setLearningModeState(mode);
        try {
            localStorage.setItem(LEARNING_MODE_KEY, mode);
        } catch { }
    }, []);

    // ── Exam/Mode Target (theo từng nhóm) ──────────────────────────────────────
    const [targetByGroup, setTargetByGroupState] = useState(loadTargetByGroup);

    const persistTargetByGroup = useCallback((next) => {
        try {
            localStorage.setItem(EXAM_TARGET_KEY, JSON.stringify(next));
        } catch { }
    }, []);

    // Target hiện tại = target của nhóm ứng với mode đang chọn.
    const currentGroup = getModeGroup(learningMode);
    const examTarget =
        targetByGroup[currentGroup] || DEFAULT_TARGET_BY_GROUP[currentGroup];

    const setExamTarget = useCallback(
        (target) => {
            setTargetByGroupState((prev) => {
                const next = { ...prev, [currentGroup]: target };
                persistTargetByGroup(next);
                return next;
            });
        },
        [currentGroup, persistTargetByGroup],
    );

    // ── Quiz State ────────────────────────────────────────────────────────────
    const [quizState, setQuizStateRaw] = useState(() => {
        try {
            const saved = localStorage.getItem(QUIZ_STATE_KEY);
            return saved ? JSON.parse(saved) : { active: false, correct: 0, total: 0, topic: "" };
        } catch {
            return { active: false, correct: 0, total: 0, topic: "" };
        }
    });

    const setQuizState = useCallback((updater) => {
        setQuizStateRaw((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            try {
                localStorage.setItem(QUIZ_STATE_KEY, JSON.stringify(next));
            } catch { }
            return next;
        });
    }, []);

    const startQuiz = useCallback((topic = "") => {
        setQuizState({ active: true, correct: 0, total: 0, topic });
        setLearningMode("quiz");
    }, [setQuizState, setLearningMode]);

    const endQuiz = useCallback(() => {
        setQuizState((prev) => ({ ...prev, active: false }));
    }, [setQuizState]);

    const recordQuizAnswer = useCallback((isCorrect) => {
        setQuizState((prev) => ({
            ...prev,
            total: prev.total + 1,
            correct: prev.correct + (isCorrect ? 1 : 0),
        }));
    }, [setQuizState]);

    // ── Build system prompt ───────────────────────────────────────────────────
    const systemPrompt = useMemo(() => {
        const parts = [];

        // Learning mode prompt
        const modeConfig = LEARNING_MODES.find((m) => m.id === learningMode);
        if (modeConfig?.systemPrompt) {
            parts.push(modeConfig.systemPrompt);
        }

        // Target prompt (math: kỳ thi / programming: ngôn ngữ / research: lĩnh vực)
        const targetConfig = findTargetConfig(examTarget);
        if (targetConfig?.systemPrompt) {
            parts.push(targetConfig.systemPrompt);
        }

        return parts.join("\n\n");
    }, [learningMode, examTarget]);

    // ── Danh sách mục tiêu phù hợp với mode đang chọn ──────────────────────────
    const availableTargets = useMemo(
        () => getTargetsForMode(learningMode),
        [learningMode],
    );

    // ── Current placeholder ───────────────────────────────────────────────────
    const placeholder = useMemo(() => {
        const modeConfig = LEARNING_MODES.find((m) => m.id === learningMode);
        return modeConfig?.placeholder || "Gửi tin nhắn tới ChatBot PIMATH...";
    }, [learningMode]);

    return {
        // Learning Mode
        learningMode,
        setLearningMode,
        // Target (theo nhóm mode)
        examTarget,
        setExamTarget,
        availableTargets,
        modeGroup: currentGroup,
        // Quiz
        quizState,
        startQuiz,
        endQuiz,
        recordQuizAnswer,
        // Computed
        systemPrompt,
        placeholder,
    };
}

export default usePimathTools;
